import React from 'react';

import {DateTime, DurationLike, FixedOffsetZone} from 'luxon';
import {FormControl, InputLabel, MenuItem, Select} from '@material-ui/core';
import {DateTimePicker, MuiPickersUtilsProvider} from "@material-ui/pickers";
import LuxonUtils from "@date-io/luxon";

import {DataMap} from '../../lib/components/map';
import styles from './map-demo.module.scss';
import {DATA_SERVICE$} from '../../environment';
import {SteppedGradient} from "../../lib/components/map/gradient";
import {SiteOverlay} from "../../lib/components/map/overlays/site-overlay";
import {LabelOverlay, GradientBarOverlay} from "../../lib/components/map/overlays";
import {ApiDataProvider, DataAggregator, MapData} from "../../lib/components/map/data-provider";
import {Site} from "../../lib/common";
import {DataService} from "../../lib/data";
import {ContourOverlay} from "../../lib/components/map/overlays/contour-overlay";
import {Tooltip, TooltipData} from "../../lib/components/map/tooltip/map-tooltip";

const BLUE_WHITE_RED = [
    '#313695',
    '#4575b4',
    '#74add1',
    '#abd9e9',
    '#e0f3f8',
    '#ffffbf',
    '#fee090',
    '#fdae61',
    '#f46d43',
    '#d73027',
    '#a50026',
];

const WHITE_GREEN = [
    '#f7fcfd',
    '#e5f5f9',
    '#ccece6',
    '#99d8c9',
    '#66c2a4',
    '#41ae76',
    '#238b45',
    '#006d2c',
    '#00441b'
];

const BROWN_BLUEGREEN = [
    '#543005',
    '#8c510a',
    '#bf812d',
    '#dfc27d',
    '#f6e8c3',
    '#f5f5f5',
    '#c7eae5',
    '#80cdc1',
    '#35978f',
    '#01665e',
    '#003c30'
];

const YELLOW_RED = [
    '#ffffcc',
    '#ffeda0',
    '#fed976',
    '#feb24c',
    '#fd8d3c',
    '#fc4e2a',
    '#e31a1c',
    '#bd0026',
    '#800026',
];

const TEMPERATURE_GRADIENT = SteppedGradient.createLinear(0, 40, BLUE_WHITE_RED);
const RH_GRADIENT = SteppedGradient.createLinear(0, 100, BROWN_BLUEGREEN, {
    dataExtendsBeyondLowerBound: false,
    dataExtendsBeyondUpperBound: false
});

type AvailableOverlays = "label" | "dot" | "contour" | "gradientBar";

type DemoConfig = {
    metric: string;
    name: string;
    gradient: SteppedGradient;
    formatter: (value: number) => string;
    duration: DurationLike;
    aggregator?: DataAggregator;
    overlays: AvailableOverlays[];
};

const rounder = (places: number) => {
    const multiplier = Math.pow(10, places);
    return (value: number) => (Math.round(value * multiplier) / multiplier).toString();
}

const ZERO_ROUNDER = rounder(0);

const CONTOUR_OVERLAYS: AvailableOverlays[] = ["label", "contour", "gradientBar"];
const DOT_OVERLAYS: AvailableOverlays[] = ["dot", "gradientBar"];

const DEMOS: DemoConfig[] = [
    {
        metric: 't_official',
        name: 'Temperature (Contour)',
        gradient: TEMPERATURE_GRADIENT,
        formatter: ZERO_ROUNDER,
        duration: {hours: 1},
        overlays: CONTOUR_OVERLAYS,
    },
    {
        metric: 't_official',
        name: 'Temperature (Dots)',
        gradient: TEMPERATURE_GRADIENT,
        formatter: ZERO_ROUNDER,
        duration: {hours: 1},
        overlays: DOT_OVERLAYS,
    },
    {
        metric: 't_official',
        name: 'Temperature (Labels Only)',
        gradient: TEMPERATURE_GRADIENT,
        formatter: ZERO_ROUNDER,
        duration: {hours: 1},
        overlays: ["label"],
    },
    {
        metric: 'sw1005',
        name: 'Soil Water Content - SW1005',
        gradient: SteppedGradient.createLinear(0, 0.6, WHITE_GREEN, {
            dataExtendsBeyondLowerBound: false
        }),
        formatter: rounder(3),
        duration: {hours: 1},
        overlays: CONTOUR_OVERLAYS,
    },
    {
        metric: 'rh',
        name: 'Relative Humidity (Contour)',
        gradient: RH_GRADIENT,
        formatter: ZERO_ROUNDER,
        duration: {hours: 1},
        overlays: CONTOUR_OVERLAYS,
    },
    {
        metric: 'rh',
        name: 'Relative Humidity (Dots)',
        gradient: RH_GRADIENT,
        formatter: ZERO_ROUNDER,
        duration: {hours: 1},
        overlays: DOT_OVERLAYS,
    },
    {
        metric: 'solarad',
        name: 'Solar Radiation (Contour)',
        gradient: SteppedGradient.createLinear(0, 1200, YELLOW_RED, {
            dataExtendsBeyondLowerBound: false
        }),
        formatter: ZERO_ROUNDER,
        duration: {hours: 1},
        overlays: CONTOUR_OVERLAYS,
    },
    {
        metric: 'p_official',
        name: 'Precipitation - 24h (Dots)',
        gradient: SteppedGradient.createLinear(0, 20, BROWN_BLUEGREEN, {
            dataExtendsBeyondLowerBound: false
        }),
        formatter: rounder(1),
        duration: {hours: 24},
        aggregator: dataPoints => dataPoints.length > 0
            ? dataPoints.map(dp => dp.value).reduce((a, b) => a + b, 0)
            : null,
        overlays: DOT_OVERLAYS,
    }
];

type State = {
    time: DateTime;
    demo: DemoConfig;
    dataService?: DataService;
    data?: MapData;
    sites?: Site[];
    tooltipData?: TooltipData;
};

const maxDate = DateTime.utc().minus({hours: 2}).startOf('hour');

export default class MapDemo extends React.Component<unknown, State> {
    constructor(props: unknown) {
        super(props);
        this.state = {
            time: maxDate,
            demo: DEMOS[0],
        };
        DATA_SERVICE$.then((dataService) => this.setState({
            dataService,
            sites: dataService.getAllSites().filter((site) =>
                site.publiclyVisible &&
                site.country === 'US' &&
                site.state !== 'AK' &&
                site.state !== 'HI'
            )
        }));
    }

    render() {
        return (
            <div className={styles.demoWrapper}>
                <h1>USCRN Map Demo</h1>
                <div className={styles.demoPanel}>
                    <div className={styles.map}>
                        <DataMap
                            formatter={this.state.demo.formatter}
                            gradient={this.state.demo.gradient}
                            data={this.state.data}
                            tooltip={<Tooltip tooltipData={this.state.tooltipData}/>}
                        >

                            {this.state.demo.overlays.map((overlay, index) => {
                                switch (overlay) {
                                    case "dot":
                                        return <SiteOverlay key={index}
                                                            overlayPosition="over-map"
                                                            setTooltipData={(td: TooltipData) => {
                                                               this.setState({
                                                                   tooltipData: td
                                                               })
                                                           }
                                        }/>
                                    case "gradientBar":
                                        return <GradientBarOverlay key={index}/>
                                    case "label":
                                        return <LabelOverlay key={index}/>
                                    case "contour":
                                        return <ContourOverlay key={index} />
                                }
                            })}

                        </DataMap>
                    </div>
                    <div className={styles.settingsWrapper}>
                        <h4>Settings</h4>
                        <MuiPickersUtilsProvider utils={LuxonUtils}>
                            <DateTimePicker
                                label="Date/Time"
                                className={styles.setting}
                                maxDate={maxDate}
                                value={this.state.time}
                                format="MMM d, y H:mm 'UTC'"
                                onChange={(time) => this.setState({time: time.setZone(FixedOffsetZone.utcInstance, {keepLocalTime: true})})}
                            />
                        </MuiPickersUtilsProvider>
                        <FormControl>
                            <InputLabel id="label">Demo Preset</InputLabel>
                            <Select
                                labelId="label"
                                value={DEMOS.indexOf(this.state.demo)}
                                onChange={(ev) =>
                                    this.setState({
                                        demo: DEMOS[ev.target.value as number],
                                    })
                                }
                                className={styles.setting}
                            >
                                {DEMOS.map((config, index) => (
                                    <MenuItem key={index.toString()} value={index}>
                                        {config.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                </div>
            </div>
        );
    }

    componentDidMount(): void {
        this.updateRefs();
    }

    componentDidUpdate(prevProps: Readonly<unknown>, prevState: Readonly<State>): void {
        this.updateRefs(prevState);
    }

    private async updateRefs(prevState?: Readonly<State>) {
        if ((
                !prevState
                || prevState.demo !== this.state.demo
                || !dateTimesEqual(prevState.time, this.state.time)
                || !arraysEqual(prevState.sites, this.state.sites)
            )
            && this.state.dataService
        ) {
            const provider = new ApiDataProvider({
                data: this.state.dataService,
                filter: dataPoints => dataPoints.filter(dp => !dp.isFlagged && typeof dp.value === 'number'),
                aggregator: this.state.demo.aggregator
            });
            this.setState({
                data: await provider.getData({
                    metric: this.state.demo.metric,
                    interval: {
                        start: this.state.time.minus(this.state.demo.duration),
                        end: this.state.time
                    },
                    sites: this.state.sites
                })
            });
        }
    }
}

function dateTimesEqual(d1: DateTime, d2: DateTime) {
    if (!d1) return !d2;
    return d1.equals(d2);
}

function arraysEqual<T>(a1: T[], a2: T[]): boolean {
    if (!a1) return !a2;
    if (!a2) return !a1;
    if (a1.length !== a2.length) return false;
    for (let i = 0; i < a1.length; i++) {
        if (a1[i] !== a2[i]) return false;
    }
    return true;
}